import { useState } from 'react';

export const DOWNLOAD_REPORT_ERROR_MESSAGE =
    'Download failed due to server issue. Please try again, contact support if issue persists';
export const DOWNLOAD_REPORT_INFO_MESSAGE = 'Please wait while we generate your report';

type DownloadSnackbarSeverity = 'info' | 'error';

const escape = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export interface DownloadColumn {
    key: string;
    label: string;
    format?: (row: any) => unknown;
}

export const useDownloadReport = (
    apiFn: () => Promise<any>,
    columns: DownloadColumn[],
    filename: string | (() => string)
) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState(DOWNLOAD_REPORT_INFO_MESSAGE);
    const [snackbarSeverity, setSnackbarSeverity] = useState<DownloadSnackbarSeverity>('info');

    const handleDownload = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        setSnackbarMessage(DOWNLOAD_REPORT_INFO_MESSAGE);
        setSnackbarSeverity('info');
        setShowSnackbar(true);
        try {
            const res  = await apiFn();
            const list = res?.list ?? res?.data?.list ?? [];

            const header = columns.map(c => escape(c.label)).join(',');
            const body   = list.map((r: any) =>
                columns.map(c => escape(c.format ? c.format(r) : r[c.key])).join(',')
            ).join('\r\n');
            const blob = new Blob([`\ufeff${header}\r\n${body}`], { type: 'text/csv;charset=utf-8;' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = typeof filename === 'function' ? filename() : filename;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);

            setShowSnackbar(false);
        } catch (err) {
            console.error('Download failed', err);
            setSnackbarMessage(DOWNLOAD_REPORT_ERROR_MESSAGE);
            setSnackbarSeverity('error');
            setShowSnackbar(true);
        } finally {
            setIsDownloading(false);
        }
    };

    const label = 'Download Report';

    return { handleDownload, isDownloading, label, showSnackbar, setShowSnackbar, snackbarMessage, snackbarSeverity };  
};
